// Facility inventory route — full port of legacy
// `src/app/[locale]/[country]/account/facility/[facilityId]/inventory/*`:
//   - feature gate `useFeatureAccess("facility.inventory.stock")` + Enterprise
//     teaser (legacy facility-inventory-page-client.tsx). The SPA's
//     FeatureAccessProvider is not mounted globally yet, so it is mounted
//     locally around the gated content (same approach as provider/team.tsx).
//     Backend still enforces the real facility-role check on every request —
//     this is UX gating only.
//   - full inventory CRUD client lives in
//     `@/components/account/facility/facility-inventory-client`.
import { useMemo } from "react";

import { Trans } from "@lingui/react/macro";
import { createFileRoute } from "@tanstack/react-router";
import { Package } from "lucide-react";

import { AccountSecurityBanner } from "@/components/account/account-security-banner";
import { FacilityFeatureTeaser } from "@/components/account/facility/facility-feature-teaser";
import { FacilityInventoryClient } from "@/components/account/facility/facility-inventory-client";
import { PageHeader } from "@/components/layout/page-header";
import {
  FeatureAccessProvider,
  useFeatureAccess,
  useFeatureAccessLoading,
} from "@/components/features/feature-access-provider";
import { Skeleton } from "@/components/ui/skeleton";
import type { OwnerType } from "@/lib/features/types";

export const Route = createFileRoute("/_authenticated/account/facility/$facilityId/inventory")({
  component: FacilityInventoryRoute,
});

// Stable reference — feeds FeatureAccessProvider's memoized scope key.
const INVENTORY_PAGE_SCOPES: OwnerType[] = ["user", "facility"];

function FacilityInventoryRoute() {
  const { facilityId } = Route.useParams();

  const ownerIds = useMemo(() => ({ facility: facilityId }), [facilityId]);

  return (
    <FeatureAccessProvider activeScopes={INVENTORY_PAGE_SCOPES} ownerIds={ownerIds}>
      <GatedFacilityInventory facilityId={facilityId} />
      <AccountSecurityBanner />
    </FeatureAccessProvider>
  );
}

function GatedFacilityInventory({ facilityId }: { facilityId: string }) {
  const access = useFeatureAccess("facility.inventory.stock");
  const loading = useFeatureAccessLoading();

  if (loading || !access) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-24 rounded-xl" />
          ))}
        </div>
        <Skeleton className="h-64 w-full rounded-xl" />
      </div>
    );
  }

  const allowed =
    access.access === "included" || (access.access === "limited" && !access.exhausted);

  if (!allowed) {
    return <InventoryFeatureTeaser facilityId={facilityId} isSoon={access.access === "soon"} />;
  }

  return (
    <div className="space-y-6">
      <PageHeader title={<Trans>Inventory & Supplies</Trans>} />
      <FacilityInventoryClient facilityId={facilityId} />
    </div>
  );
}

// ─── Feature teaser (plan without inventory) ────────────────────────────────
//
// Stock/SKU tracking and low-stock flags exist (list filter + dashboard
// "Needs attention"); usage is recorded per item with a reason, not linked to
// a consultation, and there are no supplier/cost reports yet. When the
// feature is `soon` there is no waitlist endpoint, so no CTA is shown.

function InventoryFeatureTeaser({ facilityId, isSoon }: { facilityId: string; isSoon: boolean }) {
  return (
    <FacilityFeatureTeaser
      facilityId={facilityId}
      pageTitle={<Trans>Inventory & Supplies</Trans>}
      icon={Package}
      title={<Trans>Inventory management</Trans>}
      isSoon={isSoon}
      bullets={[
        { label: <Trans>Stock & SKU tracking with quantities</Trans> },
        { label: <Trans>Automatic low-stock alerts</Trans> },
        {
          label: <Trans>Usage per procedure — know exactly what each consultation uses</Trans>,
          soon: true,
        },
        { label: <Trans>Supplier reports + cost-per-use integration</Trans>, soon: true },
      ]}
    />
  );
}
